import posthog from 'posthog-js';
import type { SessionRecordingOptions } from 'posthog-js';
import {
  MASK_TEXT_SELECTOR,
  beforePaymentTelemetrySend,
  isPaymentPage,
  maskPaymentNetworkRequest,
  maskRecordedAttribute,
  maskRecordedInput,
  maskRecordedText,
} from './paymentTelemetryPrivacy';
import { hasPortalViewerMarker } from './portalViewerMarker';

export { MASK_TEXT_SELECTOR };

const key = import.meta.env.VITE_POSTHOG_KEY;
const host = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';

export const isPostHogEnabled = Boolean(key);
export const posthogEnvironment: string = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE;

/** Super properties the buyer surface registers; both are cleared on the way back to staff routes. */
const CUSTOMER_SURFACE_PROPERTIES = ['customer_surface', 'quote_id'] as const;

export const SESSION_RECORDING_OPTIONS = {
  // Every input and every marked text node passes through these, and they only mask on the buyer
  // surface (and password fields everywhere): staff replays show what the rep sees and types.
  maskAllInputs: true,
  maskInputFn: maskRecordedInput,
  maskTextClass: 'ph-mask',
  maskTextSelector: MASK_TEXT_SELECTOR,
  maskTextFn: maskRecordedText,
  // The card fields are a cross-origin Chargebee.js iframe, so blocking every iframe is what keeps
  // PAN and CVV structurally unrecordable. The anchors carry the buyer's capability token.
  blockSelector: 'iframe, a[href*="/q/"], a[href*="confirmAcceptance"]',
  recordCrossOriginIframes: false,
  recordHeaders: false,
  recordBody: false,
  maskCapturedNetworkRequestFn: maskPaymentNetworkRequest,
  // Text masking never reaches attributes, and several `aria-label`s on this surface are built
  // from quote data on purpose (they are tested a11y contracts).
  maskAttributeFn: maskRecordedAttribute,
} satisfies SessionRecordingOptions;

/**
 * rrweb absolutizes every relative href, so an in-page anchor on the buyer surface is serialized
 * with the link token in front of its fragment, and a blocked element keeps its attributes. The
 * recorder gzips `$snapshot` payloads before `before_send` sees them, so the redaction can only
 * reach those attributes while the payload is still plain JSON: compression is turned off for the
 * buyer surface only. The option is read per event, so flipping it on a route change is enough.
 */
export function sessionRecordingOptionsFor(customerSurface: boolean) {
  return { ...SESSION_RECORDING_OPTIONS, compress_events: !customerSurface } satisfies SessionRecordingOptions;
}

/**
 * Makes a buyer session findable by the quote it belongs to. The quote's uuid is registered, never
 * the link token: the token is the capability that opens the quote.
 */
export function registerCustomerSurface(quoteId: string | null = null) {
  if (!isPostHogEnabled) return;
  posthog.register({ customer_surface: true });
  // register() merges, so without this an unidentified buyer route would keep carrying the quote
  // id of the last one that loaded.
  if (quoteId) posthog.register({ quote_id: quoteId });
  else posthog.unregister('quote_id');
}

export function unregisterCustomerSurface() {
  if (!isPostHogEnabled) return;
  for (const property of CUSTOMER_SURFACE_PROPERTIES) posthog.unregister(property);
}

function cookieDomains(hostname: string): string[] {
  const labels = hostname.split('.');
  const parents = labels.slice(0, -1).map((_, i) => `.${labels.slice(i).join('.')}`);
  return ['', hostname, ...parents];
}

function clearStorageKeys(storage: Storage, owns: (name: string) => boolean) {
  const names = Array.from({ length: storage.length }, (_, i) => storage.key(i));
  for (const name of names) {
    if (name !== null && owns(name)) storage.removeItem(name);
  }
}

/**
 * Removes what an earlier, persistent PostHog left in this browser for our project: the `ph_<token>_*`
 * cookie and storage entries and the opt-out record. The cookie may have been written for a parent
 * domain (cross-subdomain cookies), so it is expired on every domain it could have been set on.
 * Other projects' keys and the app's own storage are left alone.
 */
export function clearStoredPostHogState(token: string, win: Window = window) {
  const owns = (name: string) => name.startsWith(`ph_${token}_`) || name === `__ph_opt_in_out_${token}`;
  for (const read of [() => win.localStorage, () => win.sessionStorage]) {
    try {
      clearStorageKeys(read(), owns);
    } catch {
      // blocked storage holds nothing to clear
    }
  }
  try {
    const names = win.document.cookie.split(';').map((pair) => pair.split('=')[0].trim()).filter(owns);
    for (const name of names) {
      for (const domain of cookieDomains(win.location.hostname)) {
        win.document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
      }
    }
  } catch {
    // cookies disabled
  }
}

/**
 * A buyer on a customer link is kept in memory only: no cookie, nothing in localStorage or
 * sessionStorage, so the tab leaves nothing behind. The trade-off is that PostHog forgets the buyer
 * between page loads, so each load is a new anonymous id and a new `$session_id` (replays stay whole
 * within a load and are joined up by the `quote_id` super property). The one-time cleanup of an
 * earlier visit's state skips a browser that has signed in to the portal, so a rep previewing their
 * own link keeps the staff identity the portal stored.
 */
export function customerPersistenceFor(customerSurface: boolean) {
  return customerSurface ? { persistence: 'memory' as const } : {};
}

export function registerBaseSuperProperties() {
  if (!isPostHogEnabled) return;
  const props: Record<string, unknown> = { environment: posthogEnvironment };
  if (navigator.webdriver) {
    props.automation = true;
  }
  posthog.register(props);
}

if (isPostHogEnabled) {
  const customerEntry = isPaymentPage();
  if (customerEntry && !hasPortalViewerMarker()) clearStoredPostHogState(key as string);
  posthog.init(key as string, {
    ...customerPersistenceFor(customerEntry),
    api_host: host,
    ui_host: host,
    autocapture: true,
    capture_pageview: false,
    person_profiles: 'identified_only',
    session_recording: sessionRecordingOptionsFor(customerEntry),
    before_send: beforePaymentTelemetrySend,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    capture_performance: { web_vitals: true },
    capture_dead_clicks: true,
    // The wizard logs whole API errors (request body: contact email, account and line data), the
    // replay console plugin records every level with no filter, and staff snapshots are gzipped
    // before before_send can redact them. Pinned off so the project toggle cannot turn it on.
    enable_recording_console_log: false,
    // default UA/webdriver bot filter would drop the automation traffic we tag via registerBaseSuperProperties
    opt_out_useragent_filter: true,
  });
  registerBaseSuperProperties();
}

export { posthog };
